import { useEffect, useState } from 'react';
import { Alert, ButtonGroup, Card, Table, ToggleButton } from 'react-bootstrap';
import axios from 'axios';
import {
  COMPETITORS, COMPETITORS_CHECKED, COMPETITOR_PRICES, FEATURE_ROWS, SOURCES, TALK_TRACK, WHEN_NOT_TO_PITCH, costOver,
} from '../data/competitors';

const SHADE_OURS = { '--bs-table-bg': 'var(--bs-primary-bg-subtle)', '--bs-table-color': 'var(--bs-body-color)' };
const SHADE_GAP = { '--bs-table-bg': 'var(--bs-warning-bg-subtle)', '--bs-table-color': 'var(--bs-body-color)' };
const inr = (v) => `₹${Math.round(v).toLocaleString('en-IN')}`;

/** What a plan does for a business that makes things: production, quality, weighbridge and job work are not in every plan. */
function ownNote(plan) {
  const has = new Set(plan.features || []);
  const works = ['PRODUCTION', 'QUALITY_CONTROL', 'WEIGHBRIDGE', 'JOB_WORK'];
  const store = has.has('ONLINE_STORE') ? ' Includes the online store.' : '';
  if (works.every((k) => has.has(k))) return `Includes production, quality, weighbridge and job work.${store}`;
  if (works.some((k) => has.has(k))) return `Some of production, quality, weighbridge and job work.${store}`;
  return `Buying, stock, sales and accounts; no production, quality, weighbridge or job work.${store}`;
}

/**
 * Account Management > Compare: how iBigDo stands against the products customers ask about, for account managers. Our own
 * prices come from the live plan list; what the others charge and do is in data/competitors.js, dated, with its sources.
 */
function AccountManagerCompare() {
  const [plans, setPlans] = useState(null);
  const [years, setYears] = useState(3);

  useEffect(() => {
    axios.get('/api/account-manager/plans')
      .then((r) => setPlans(r.data.plans || []))
      .catch(() => setPlans([]));
  }, []);

  const rows = [
    ...(plans || []).map((p) => ({
      id: `ibigdo-${p.id}`, name: `iBigDo ${p.name}`, ours: true, yearly: Number(p.priceYearly),
      users: p.maxUsers == null ? 'Unlimited users' : `${p.maxUsers} users`, note: `${ownNote(p)} Hosting, backups, updates and support included.`,
    })),
    ...COMPETITOR_PRICES,
  ];

  return (
    <div className="mt-3">
      <p className="text-muted">
        For when a customer asks why we cost more than Tally, Busy, Zoho, Marg or Vyapar. What they charge and do was read from
        vendor and reseller pages on {COMPETITORS_CHECKED}.
      </p>
      <Alert variant="warning" className="small">
        Their prices and features change, and reseller prices differ. Say &ldquo;about&rdquo; and have the customer check the
        vendor&apos;s own page before they rely on a figure. If you find something different, raise a Support Ticket and we will
        correct this page.
      </Alert>

      <Card className="mb-3">
        <Card.Header><h6 className="mb-0">&ldquo;Why are you costlier?&rdquo;</h6></Card.Header>
        <Card.Body>
          <ol className="mb-0">
            {TALK_TRACK.map((t) => (
              <li key={t.say} className="mb-2"><strong>{t.say}</strong> {t.detail}</li>
            ))}
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h6 className="mb-0">What a customer pays</h6>
          <ButtonGroup size="sm" aria-label="Years">
            {[1, 3, 5].map((y) => (
              <ToggleButton key={y} id={`compare-years-${y}`} type="radio" variant="outline-primary" name="compare-years"
                value={y} checked={years === y} onChange={() => setYears(y)}>
                {y} year{y === 1 ? '' : 's'}
              </ToggleButton>
            ))}
          </ButtonGroup>
        </Card.Header>
        <Card.Body>
          <p className="text-muted small">
            Rupees, before GST. A one-time licence is counted with its yearly renewal from the second year, which is the cheapest way
            to run it. It does not count a server, backups, hosting or the person who looks after them.
          </p>
          <Table size="sm" bordered responsive className="mb-0 align-middle">
            <thead>
              <tr><th>Product</th><th>Who it is for</th><th className="text-end">Over {years} year{years === 1 ? '' : 's'}</th><th className="text-end">A year, on average</th><th>Note</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const total = costOver(r, years);
                return (
                  <tr key={r.id} style={r.ours ? SHADE_OURS : undefined}>
                    <td><strong>{r.name}</strong></td>
                    <td>{r.users}</td>
                    <td className="text-end">{inr(total)}</td>
                    <td className="text-end">{inr(total / years)}</td>
                    <td className="small">{r.note}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><h6 className="mb-0">What each one does</h6></Card.Header>
        <Card.Body>
          <p className="text-muted small">
            &ldquo;Not checked&rdquo; means no page we read answered it, which is not the same as &ldquo;no&rdquo;. Nearly every product
            claims bills of materials, batches and roles, so ask the customer to try the part that matters to them. Where we are
            behind is shaded.
          </p>
          <Table size="sm" bordered responsive className="mb-0 align-top">
            <thead>
              <tr><th />{['iBigDo', ...COMPETITORS].map((c) => <th key={c}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {FEATURE_ROWS.map((r) => (
                <tr key={r.label}>
                  <td><strong>{r.label}</strong></td>
                  <td style={r.gap ? SHADE_GAP : SHADE_OURS}>{r.ibigdo}</td>
                  {r.others.map((t, i) => (
                    <td key={COMPETITORS[i]} className={t === 'Not checked' ? 'text-muted' : undefined}>{t}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><h6 className="mb-0">When not to pitch iBigDo</h6></Card.Header>
        <Card.Body>
          <ul className="mb-0">{WHEN_NOT_TO_PITCH.map((t) => <li key={t}>{t}</li>)}</ul>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><h6 className="mb-0">Where this came from</h6></Card.Header>
        <Card.Body>
          <ul className="small mb-2">
            {SOURCES.map(([label, url]) => (
              <li key={url}><a href={url} target="_blank" rel="noopener noreferrer">{label}</a></li>
            ))}
          </ul>
          <p className="text-muted small mb-0">
            Found a price or a feature that differs? Raise a Support Ticket with where you saw it. What customers tell you in your
            own market surveys is worth more than these pages.
          </p>
        </Card.Body>
      </Card>
    </div>
  );
}

export default AccountManagerCompare;
